import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { GlobalVariable } from '../helpers/settings';
import { Result } from '../models/general';
import { CapConfig, CashBook, cashBookFilter, CashJournalModel } from '../models/cash-book';
import { AuthService } from './auth.service';
import { PaginationOption } from '../models/pagination.model';
import { RefreshVerificationService } from './refresh-verification.service';

export interface PaginationData {
	docs: CashBook[];
	total: number;
	limit: number;
	page: number;
	pages: number;
}

export interface LastCashBookData {
	totalInputs?: number;
	totalOutputs?: number;
	balanceData?: number;
}

@Injectable({
	providedIn: 'root'
})
export class CashBookService {
	readonly BASE_URL = `${GlobalVariable.server.host}/cash-books`;

	constructor(
		private httpClient: HttpClient,
		private authService: AuthService,
		private refreshVerifService: RefreshVerificationService
	) {}

	find(
		fromAppComponent: boolean,
		route: string,
		projectId: string,
		attributes: string = null
	): Observable<CashBook[]> {
		// !! cette route est juste pour le component chartModel
		const globalChartModelInfo = GlobalVariable['CHART_MODEL'];
		let params = new HttpParams()
			.set('fromConfigComponent', true)
			.set('componentRoute', globalChartModelInfo.route)
			.set('actionName', null)
			.set('attributes', attributes ?? null);
		return this.refreshVerifService.buidResult(
			this.httpClient.get<CashBook[]>(`${this.BASE_URL}/find-from-config`, { params })
		);
	}

	findById(id: string, projectId: string): Observable<CashBook> {
		const params = new HttpParams().set('projectId', projectId);
		return this.refreshVerifService.buidResult(
			this.httpClient.get<CashBook>(`${this.BASE_URL}/find-by-id/${id}`, { params })
		);
	}
	findLastCashBook(projectId: string): Observable<CashBook> {
		const params = new HttpParams().set('projectId', projectId);
		return this.refreshVerifService.buidResult(
			this.httpClient.get<CashBook>(`${this.BASE_URL}/find-last-cashbook/${projectId}`, { params })
		);
	}

	findByProjectId(
		id: string,
		paginationOption: PaginationOption,
		filter?: cashBookFilter
	): Observable<PaginationData> {
		return this.refreshVerifService.buidResult(
			this.httpClient.post<PaginationData>(`${this.BASE_URL}/find-by-project-id/${id}`, {
				paginationOption,
				filter
			})
		);
	}
	findAllByProjectId(id: string): Observable<CashBook[]> {
		return this.refreshVerifService.buidResult(
			this.httpClient.get<CashBook[]>(`${this.BASE_URL}/find-all-by-project-id/${id}`)
		);
	}

	getCapBalanceData(id: string): Observable<LastCashBookData> {
		return this.refreshVerifService.buidResult(
			this.httpClient.get<LastCashBookData>(`${this.BASE_URL}/get-cap-balance-data/${id}`)
		);
	}

	create(cashBook: Partial<CashBook>): Observable<{ message: string; cashBookId: string }> {
		cashBook.user = this.authService.getCurrentUser();
		return this.refreshVerifService.buidResult(
			this.httpClient.post<{ message: string; cashBookId: string }>(`${this.BASE_URL}/create`, cashBook)
		);
	}

	edit(cashBook: Partial<CashBook>, projectId: string): Observable<Result> {
		const params = new HttpParams().set('projectId', projectId);
		return this.refreshVerifService.buidResult(
			this.httpClient.put<Result>(`${this.BASE_URL}/edit`, cashBook, { params })
		);
	}

	delete(cashBookId: string, projectId: string): Observable<Result> {
		const params = new HttpParams().set('projectId', projectId);
		return this.refreshVerifService.buidResult(
			this.httpClient.delete<Result>(`${this.BASE_URL}/delete/${cashBookId}`, { params })
		);
	}
}
